import React, { useContext } from 'react'
import { CIRCULATION_METHOD_OPTIONS } from '../store/constants'
import { Span } from 'ui/Typography'
import { AppState } from 'store/GlobalState'

// This file accommodates the addition of the Instant Bundle product type,
// and relies on the showInstantBundles setting from GlobalState.js

/**
 * Controls which options are available based on showInstantBundles setting
 * Removes instant_bundle ModelSwitcher option and data table columns if showInstantBundles is false
*/
export const useAvailableCirculationMethods = (showInstantBundles: boolean) => {
  const options = { ...CIRCULATION_METHOD_OPTIONS }

  if (!showInstantBundles) {
    delete options.instant_bundle
  }

  return options
}

/**
 * Hide Circulations
*/
export const shouldHideCirculations = (
  product,
  shouldHideFlexCirculations,
  showInstantBundles,
):boolean => {
  if (!shouldHideFlexCirculations) return false

  const behavior = getTitlePerformanceBehavior(product, showInstantBundles)

  return behavior === 'FLEX'
}

/**
 * Creates Model label for Daily Circulations Overview Table
*/
export const getModelLabel = (product, showInstantBundles) => {
  const behavior = getTitlePerformanceBehavior(product, showInstantBundles)

  switch (behavior) {
    case 'FLEX':
      return CIRCULATION_METHOD_OPTIONS.flex.label
    case 'INSTANT':
      return CIRCULATION_METHOD_OPTIONS.instant.label
    case 'INSTANT_BUNDLE':
      return CIRCULATION_METHOD_OPTIONS.instant_bundle.label
    default:
      return null
  }
}

/**
 * Component that displays either circulationMethods or accessBehavior
 * Uses accessBehavior when showInstantBundles is true
 * Uses circulationMethods when showInstantBundles is false (legacy behavior)
*/
export const useCirculationsBehavior = () => {
  const {
      settings: {
        showInstantBundles
      },
  } = useContext(AppState)

  return (circulationMethods: string, accessBehavior: string) => {
    const behavior = showInstantBundles ? accessBehavior : circulationMethods
    
    if (!behavior) return null
    
    // If method is an array
    if (Array.isArray(behavior)) {
      return (
        <>
          {behavior.map((m, i) => (
            <Span key={m}>
              {CIRCULATION_METHOD_OPTIONS[m.toLowerCase()]?.label}
              {behavior.length - 1 !== i && ', '}
            </Span>
          ))}
        </>
      )
    }
    
    // If method is a single string
    return (
      <Span>
        {CIRCULATION_METHOD_OPTIONS[behavior.toLowerCase()]?.label}
      </Span>
    )
  }
}

/**
 * Function that unifies behavior logic across reports
 * Uses accessBehavior when showInstantBundles is true
 * Uses purchaseModel when showInstantBundles is false
*/
export const getTitlePerformanceBehavior = (product, showInstantBundles) => {
  if (showInstantBundles && product.accessBehavior) {
    return product.accessBehavior.toUpperCase()
  }

  if (product.purchaseModel) {
    const model = product.purchaseModel.toUpperCase()
    switch (model) {
      case 'PPU':
        return 'INSTANT'
      case 'EST':
        return 'FLEX'
      default:
        return null
    }
  }

  return null
}